/**
 * TuningPanel - Configure microtonal tuning (v2.0)
 * Supports Custom EDO, presets, and Scala .scl import.
 */

import { useState, useEffect, useRef } from 'react';
import { useConfigStore } from '@/store/configStore';
import { tuningPresets } from '@/lib/tuningPresets';
import { parseScalaFile } from '@/lib/scalaParser';

type TuningSource = 'custom_edo' | 'preset' | 'import_scl';

export function TuningPanel() {
  const tuningEnabled = useConfigStore((state) => state.tuningEnabled);
  const tuningTable = useConfigStore((state) => state.tuningTable);
  const noteMapping = useConfigStore((state) => state.noteMapping);
  const setEdoTuning = useConfigStore((state) => state.setEdoTuning);
  const setTuningTable = useConfigStore((state) => state.setTuningTable);
  const setPitchBendRange = useConfigStore((state) => state.setPitchBendRange);
  const loadTuning = useConfigStore((state) => state.loadTuning);
  const setPitchValue = useConfigStore((state) => state.setPitchValue);
  const tuningBaseFrequency = useConfigStore((state) => state.tuningBaseFrequency);
  const setTuningBaseFrequency = useConfigStore((state) => state.setTuningBaseFrequency);
  const updateNoteMappingBase = useConfigStore((state) => state.updateNoteMappingBase);
  const stagger = useConfigStore((state) => state.stagger);
  const setStagger = useConfigStore((state) => state.setStagger);
  const setIntervalCents = useConfigStore((state) => state.setIntervalCents);

  // Zones (for per-zone interval editing)
  const zones = useConfigStore((state) => state.zones);
  const updateZone = useConfigStore((state) => state.updateZone);
  const setZonePreview = useConfigStore((state) => state.setZonePreview);

  // Scope: 'global' or zone index
  const [scope, setScope] = useState<'global' | number>('global');

  // Reset scope if selected zone was deleted
  useEffect(() => {
    if (typeof scope === 'number' && scope >= zones.length) {
      setScope('global');
    }
  }, [scope, zones.length]);

  // Highlight zone on keyboard when zone scope is selected
  useEffect(() => {
    if (typeof scope === 'number') {
      const zone = zones[scope];
      if (zone) setZonePreview(zone.bounds);
    } else {
      setZonePreview(null);
    }
    return () => setZonePreview(null);
  }, [scope, zones, setZonePreview]);

  const isZoneScope = typeof scope === 'number';
  const selectedZone = isZoneScope ? zones[scope] : undefined;
  const hasZoneMapping = selectedZone?.note_mapping !== undefined;
  const effectiveMapping = (isZoneScope && selectedZone?.note_mapping) ? selectedZone.note_mapping : noteMapping;

  const handleEnableZoneIntervals = (enabled: boolean) => {
    if (!isZoneScope || !selectedZone) return;
    if (enabled) {
      updateZone(scope, { ...selectedZone, note_mapping: { ...noteMapping } });
    } else {
      const { note_mapping: _, ...zoneWithout } = selectedZone;
      updateZone(scope, zoneWithout as typeof selectedZone);
    }
  };

  const handleZoneIntervalCents = (axis: 'q' | 'r', centsX10: number) => {
    if (!isZoneScope || !selectedZone?.note_mapping) return;
    const updated = {
      ...selectedZone.note_mapping,
      ...(axis === 'q' ? { interval_q_cents: centsX10 } : { interval_r_cents: centsX10 }),
    };
    updateZone(scope, { ...selectedZone, note_mapping: updated });
  };

  const [tuningSource, setTuningSource] = useState<TuningSource>('custom_edo');
  const [scalaError, setScalaError] = useState<string>('');
  const scalaFileInputRef = useRef<HTMLInputElement>(null);

  const divisions = tuningTable?.notes_per_period ?? 12;
  const periodCents = tuningTable?.period_cents ?? 12000;
  const stepCents = periodCents / divisions / 10; // display in cents

  const handleEdoChange = (value: number) => {
    if (value >= 1 && value <= 128) {
      setEdoTuning(value);
    }
  };

  const handleScaleMaskToggle = (index: number) => {
    if (!tuningTable) return;
    const newMask = [...tuningTable.scale_mask];
    newMask[index] = !newMask[index];
    setTuningTable({
      ...tuningTable,
      scale_mask: newMask,
    });
  };

  const handlePresetChange = (presetName: string) => {
    const preset = tuningPresets.find((p) => p.name === presetName);
    if (!preset) return;
    loadTuning(preset.tuningTable, preset.suggestedIntervals);
  };

  const handleScalaImport = async () => {
    scalaFileInputRef.current?.click();
  };

  const handleScalaFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScalaError('');

    try {
      const content = await file.text();
      const result = parseScalaFile(content);

      if (!result.valid || !result.tuningTable) {
        setScalaError(result.errors?.join('; ') ?? 'Unknown parse error');
        return;
      }

      loadTuning(result.tuningTable);
    } catch (error) {
      setScalaError(error instanceof Error ? error.message : 'Failed to read file');
    } finally {
      if (scalaFileInputRef.current) {
        scalaFileInputRef.current.value = '';
      }
    }
  };

  const disabledStyle = {
    opacity: 0.5,
    pointerEvents: 'none' as const,
  };

  // Group presets by category
  const categories = [...new Set(tuningPresets.map((p) => p.category))];

  const radioStyle = (active: boolean) => ({
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    cursor: tuningEnabled ? 'pointer' : 'not-allowed',
    borderRadius: '4px',
    backgroundColor: active ? '#e7f3ff' : 'transparent',
    border: `1px solid ${active ? '#007bff' : 'transparent'}`,
  });

  return (
    <>
      {/* All controls disabled when tuning is off (mode selector is in Layout tab) */}
      <div style={!tuningEnabled ? disabledStyle : undefined}>

        {/* Zone scope selector (only when zones exist) */}
        {zones.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="tuning-scope"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}
            >
              Scope:
            </label>
            <select
              id="tuning-scope"
              value={typeof scope === 'number' ? scope.toString() : 'global'}
              onChange={(e) => {
                const val = e.target.value;
                setScope(val === 'global' ? 'global' : parseInt(val, 10));
              }}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
              }}
            >
              <option value="global">Global</option>
              {zones.map((z, i) => (
                <option key={i} value={i.toString()}>
                  Zone {i + 1}: {z.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Zone custom intervals toggle */}
        {isZoneScope && selectedZone && (
          <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '6px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasZoneMapping}
                onChange={(e) => handleEnableZoneIntervals(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>
                Use zone-specific intervals
              </span>
            </label>
            {!hasZoneMapping && (
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
                Currently using global intervals
              </div>
            )}
          </div>
        )}

        {/* Tuning source selection */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
            Tuning Source:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <label style={radioStyle(tuningSource === 'custom_edo')}>
              <input
                type="radio"
                name="tuning-source"
                checked={tuningSource === 'custom_edo'}
                onChange={() => setTuningSource('custom_edo')}
                disabled={!tuningEnabled}
              />
              Custom EDO
            </label>
            <label style={radioStyle(tuningSource === 'preset')}>
              <input
                type="radio"
                name="tuning-source"
                checked={tuningSource === 'preset'}
                onChange={() => setTuningSource('preset')}
                disabled={!tuningEnabled}
              />
              Preset Library
            </label>
            <label style={radioStyle(tuningSource === 'import_scl')}>
              <input
                type="radio"
                name="tuning-source"
                checked={tuningSource === 'import_scl'}
                onChange={() => setTuningSource('import_scl')}
                disabled={!tuningEnabled}
              />
              Import .scl File
            </label>
          </div>
        </div>

        {/* Custom EDO controls */}
        {tuningSource === 'custom_edo' && (
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="edo-divisions"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              EDO Divisions:
            </label>
            <input
              id="edo-divisions"
              type="number"
              min="1"
              max="128"
              value={divisions}
              onChange={(e) => handleEdoChange(parseInt(e.target.value, 10))}
              disabled={!tuningEnabled}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                marginBottom: '8px',
              }}
            />
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[12, 19, 22, 31].map((n) => (
                <button
                  key={n}
                  onClick={() => handleEdoChange(n)}
                  disabled={!tuningEnabled}
                  style={{
                    padding: '4px 12px',
                    fontSize: '13px',
                    border: `1px solid ${divisions === n ? '#007bff' : '#e9ecef'}`,
                    borderRadius: '4px',
                    cursor: tuningEnabled ? 'pointer' : 'not-allowed',
                    backgroundColor: divisions === n ? '#e7f3ff' : '#fff',
                    color: divisions === n ? '#007bff' : '#333',
                  }}
                >
                  {n}-EDO
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preset library */}
        {tuningSource === 'preset' && (
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="tuning-preset"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Select Preset:
            </label>
            <select
              id="tuning-preset"
              onChange={(e) => handlePresetChange(e.target.value)}
              disabled={!tuningEnabled}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
              defaultValue=""
            >
              <option value="" disabled>Choose a tuning...</option>
              {categories.map((cat) => (
                <optgroup key={cat} label={cat}>
                  {tuningPresets
                    .filter((p) => p.category === cat)
                    .map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        {/* Import .scl file */}
        {tuningSource === 'import_scl' && (
          <div style={{ marginBottom: '16px' }}>
            <input
              ref={scalaFileInputRef}
              type="file"
              accept=".scl"
              onChange={handleScalaFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={handleScalaImport}
              disabled={!tuningEnabled}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#6f42c1',
                border: 'none',
                borderRadius: '4px',
                cursor: tuningEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              Choose .scl File...
            </button>
            {scalaError && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                fontSize: '12px',
                borderRadius: '4px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
              }}>
                {scalaError}
              </div>
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d', lineHeight: '1.6' }}>
              Create or browse .scl files:
              {' '}<a href="https://sevish.com/scaleworkshop/" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Scale Workshop</a>
              {' | '}<a href="https://www.huygens-fokker.org/scala/scl_format.html" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Scala format spec</a>
              {' | '}<a href="https://www.huygens-fokker.org/docs/scalesdir.txt" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>Scale archive</a>
            </div>
          </div>
        )}

        {/* Period & step info */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: '1.6',
          }}>
            <div>Period: {(periodCents / 10).toFixed(1)} cents</div>
            <div>Step size: {stepCents.toFixed(1)} cents</div>
            <div>Notes per period: {divisions}</div>
          </div>
        </div>

        {/* Reference note & frequency */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
            Reference Pitch:
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="tuning-base-note"
                style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}
              >
                MIDI Note (at origin key):
              </label>
              <select
                id="tuning-base-note"
                value={noteMapping.base_note}
                onChange={(e) => {
                  const note = parseInt(e.target.value, 10);
                  updateNoteMappingBase(note);
                  // Auto-set frequency to 12-TET value for this note
                  setTuningBaseFrequency(Math.round(440 * Math.pow(2, (note - 69) / 12) * 100) / 100);
                }}
                disabled={!tuningEnabled}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '14px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                {Array.from({ length: 128 }, (_, i) => {
                  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
                  const name = (names[i % 12] ?? '') + (Math.floor(i / 12) - 1);
                  return <option key={i} value={i}>{i} ({name})</option>;
                })}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="tuning-base-freq"
                style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}
              >
                Frequency (Hz):
              </label>
              <input
                id="tuning-base-freq"
                type="number"
                min="1"
                max="20000"
                step="0.01"
                value={tuningBaseFrequency}
                onChange={(e) => {
                  const f = parseFloat(e.target.value);
                  if (!isNaN(f)) setTuningBaseFrequency(f);
                }}
                disabled={!tuningEnabled}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '14px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
            Degree 0 (1/1) at the origin key = {tuningBaseFrequency} Hz. Change frequency for alternative tuning (e.g. A=432).
          </div>
        </div>

        {/* Pitch bend range */}
        <div style={{ marginBottom: '16px' }}>
          <label
            htmlFor="pitch-bend-range"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Pitch Bend Range (semitones):
          </label>
          <input
            id="pitch-bend-range"
            type="number"
            min="1"
            max="96"
            value={noteMapping.pitch_bend_range ?? 48}
            onChange={(e) => setPitchBendRange(parseInt(e.target.value, 10))}
            disabled={!tuningEnabled}
            style={{
              width: '100%',
              padding: '6px',
              fontSize: '14px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
            }}
          />
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
            Auto-calculated from tuning. Set the same value in your synth/DAW pitch bend range setting.
          </div>
        </div>

        {/* Axis intervals */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
            Axis Intervals (cents){isZoneScope && hasZoneMapping ? ` — Zone: ${selectedZone?.name}` : ''}:
          </div>
          {isZoneScope && !hasZoneMapping ? (
            <div style={{ padding: '8px', fontSize: '12px', color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              Using global intervals. Enable zone-specific intervals above to customize.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="tuning-interval-q" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                    Q-axis:
                  </label>
                  <input
                    id="tuning-interval-q"
                    type="number"
                    step="0.1"
                    value={((effectiveMapping.interval_q_cents ?? 0) / 10).toFixed(1)}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) {
                        const centsX10 = Math.round(v * 10);
                        if (isZoneScope && hasZoneMapping) {
                          handleZoneIntervalCents('q', centsX10);
                        } else {
                          setIntervalCents('q', centsX10);
                        }
                      }
                    }}
                    disabled={!tuningEnabled}
                    style={{
                      width: '100%',
                      padding: '6px',
                      fontSize: '14px',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="tuning-interval-r" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                    R-axis:
                  </label>
                  <input
                    id="tuning-interval-r"
                    type="number"
                    step="0.1"
                    value={((effectiveMapping.interval_r_cents ?? 0) / 10).toFixed(1)}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) {
                        const centsX10 = Math.round(v * 10);
                        if (isZoneScope && hasZoneMapping) {
                          handleZoneIntervalCents('r', centsX10);
                        } else {
                          setIntervalCents('r', centsX10);
                        }
                      }
                    }}
                    disabled={!tuningEnabled}
                    style={{
                      width: '100%',
                      padding: '6px',
                      fontSize: '14px',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
                Musical interval per hex step. For all degrees to be reachable, degree steps must be coprime (GCD=1).
              </div>
            </>
          )}
        </div>

        {/* Coordinate Stagger */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
            Stagger:
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="stagger-q-input" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                q (per r-step):
              </label>
              <input
                id="stagger-q-input"
                type="number"
                min="-4"
                max="4"
                step="1"
                value={stagger.q}
                onChange={(e) => setStagger(parseInt(e.target.value, 10), stagger.r)}
                disabled={!tuningEnabled}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '14px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="stagger-r-input" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                r (per q-step):
              </label>
              <input
                id="stagger-r-input"
                type="number"
                min="-4"
                max="4"
                step="1"
                value={stagger.r}
                onChange={(e) => setStagger(stagger.q, parseInt(e.target.value, 10))}
                disabled={!tuningEnabled}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '14px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
            Additional coordinate offset per axis step. Rotates the note layout on the grid.
          </div>
        </div>

        {/* Scale mask */}
        {tuningTable && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
              Scale Highlight ({tuningTable.notes_per_period} degrees):
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '6px' }}>
              Uncheck degrees to dim their LED color. Does not affect note mapping.
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {tuningTable.scale_mask.map((inScale, i) => (
                <label
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    fontSize: '12px',
                    cursor: tuningEnabled ? 'pointer' : 'not-allowed',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    backgroundColor: inScale ? '#e7f3ff' : '#f8f9fa',
                    border: `1px solid ${inScale ? '#007bff' : '#dee2e6'}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={inScale}
                    onChange={() => handleScaleMaskToggle(i)}
                    disabled={!tuningEnabled}
                    style={{ width: '12px', height: '12px' }}
                  />
                  {i}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Pitch Editor */}
        {tuningEnabled && tuningTable && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
              Pitch Editor ({tuningTable.notes_per_period} degrees):
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '6px' }}>
              Fine-tune individual pitch values in cents. Degree 0 is always 0.
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {tuningTable.pitches.map((pitch, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                  <span style={{ fontSize: '10px', color: '#6c757d' }}>{i}</span>
                  <input
                    type="number"
                    step="0.1"
                    value={(pitch / 10).toFixed(1)}
                    readOnly={i === 0}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) setPitchValue(i, Math.round(v * 10));
                    }}
                    style={{
                      width: '62px',
                      padding: '3px 4px',
                      fontSize: '11px',
                      textAlign: 'center',
                      border: '1px solid #dee2e6',
                      borderRadius: '3px',
                      backgroundColor: i === 0 ? '#f8f9fa' : '#fff',
                      color: i === 0 ? '#6c757d' : '#333',
                    }}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                if (!tuningTable) return;
                const N = tuningTable.notes_per_period;
                const step = Math.round(tuningTable.period_cents / N);
                const pitches = Array.from({ length: N }, (_, i) => i * step);
                setTuningTable({ ...tuningTable, pitches });
              }}
              style={{
                padding: '4px 12px',
                fontSize: '12px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                color: '#333',
              }}
            >
              Reset to Equal
            </button>
          </div>
        )}

        {/* Info box */}
        <div style={{
          padding: '8px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#0c5460',
        }}>
          BRI exported as v2.0 when tuning is enabled. Pitch bend range must match synth MPE settings.
        </div>
      </div>
    </>
  );
}
